/**
 * Performance Change Requests Charts Component (CR-6310)
 *
 * Displays bar charts for Change Requests by type (Date/Price/Qty) and
 * initiator (Customer vs Supplier). Based on legacy CR-6002 requirements:
 * - Initiator attribution from company_type (status transitions)
 * - Both supplier- and customer-initiated series shown
 * - Clickable bars navigate to change requests detail view
 */
import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import type { PerformanceScorecardChangeRequests } from '../../lib/api'

interface DrilldownFilters {
  supplierId: number | null
  startDate: string
  endDate: string
}

interface ChangeRequestsChartsProps {
  changeRequests: PerformanceScorecardChangeRequests
  period: 'ytd' | 'month'
  periodLabel: string
  /** Optional filters for drill-down navigation */
  drilldownFilters?: DrilldownFilters
}

interface ChartBarProps {
  label: string
  changeType: 'date' | 'price' | 'qty'
  customerValue: number
  supplierValue: number
  maxValue: number
  onBarClick?: (changeType: string) => void
}

function ChartBar({ label, changeType, customerValue, supplierValue, maxValue, onBarClick }: ChartBarProps) {
  const total = customerValue + supplierValue
  const customerPct = maxValue > 0 ? (customerValue / maxValue) * 100 : 0
  const supplierPct = maxValue > 0 ? (supplierValue / maxValue) * 100 : 0
  const isClickable = !!onBarClick && total > 0

  const handleClick = useCallback(() => {
    if (onBarClick && total > 0) {
      onBarClick(changeType)
    }
  }, [onBarClick, changeType, total])

  return (
    <div className="mb-4">
      <div className="flex justify-between text-sm mb-1">
        <span className="font-medium">{label}</span>
        {/* "Total: 0" beside a bar that already reads "No changes" states the
            same nothing twice, and a bare 0 invites the reading that the number
            failed to load rather than that nothing happened. Say it once. */}
        <span className="text-muted-foreground">
          {total === 0 ? 'No changes' : `Total: ${total}`}
        </span>
      </div>
      <div
        className={`flex h-6 bg-gray-100 rounded overflow-hidden ${
          isClickable ? 'cursor-pointer hover:ring-2 hover:ring-primary/20' : ''
        }`}
        onClick={handleClick}
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
        onKeyDown={isClickable ? (e) => { if (e.key === 'Enter') handleClick() } : undefined}
        title={isClickable ? 'Click to view change request details' : undefined}
      >
        {customerValue > 0 && (
          <div
            className="bg-blue-500 flex items-center justify-center text-xs text-white"
            style={{ width: `${customerPct}%`, minWidth: customerValue > 0 ? '20px' : '0' }}
            title={`Customer: ${customerValue}`}
          >
            {customerValue > 0 && customerPct > 10 ? customerValue : ''}
          </div>
        )}
        {supplierValue > 0 && (
          <div
            className="bg-orange-500 flex items-center justify-center text-xs text-white"
            style={{ width: `${supplierPct}%`, minWidth: supplierValue > 0 ? '20px' : '0' }}
            title={`Supplier: ${supplierValue}`}
          >
            {supplierValue > 0 && supplierPct > 10 ? supplierValue : ''}
          </div>
        )}
        {total === 0 && (
          <div className="flex-1 flex items-center justify-center text-xs text-muted-foreground">
            No changes
          </div>
        )}
      </div>
    </div>
  )
}

export function PerformanceChangeRequestsCharts({
  changeRequests,
  period,
  periodLabel,
  drilldownFilters,
}: ChangeRequestsChartsProps) {
  const navigate = useNavigate()
  const suffix = period === 'ytd' ? '_ytd' : '_month'

  const dateCustomer = changeRequests.date_changes[`customer${suffix}` as keyof typeof changeRequests.date_changes]
  const dateSupplier = changeRequests.date_changes[`supplier${suffix}` as keyof typeof changeRequests.date_changes]
  const priceCustomer = changeRequests.price_changes[`customer${suffix}` as keyof typeof changeRequests.price_changes]
  const priceSupplier = changeRequests.price_changes[`supplier${suffix}` as keyof typeof changeRequests.price_changes]
  const qtyCustomer = changeRequests.qty_changes[`customer${suffix}` as keyof typeof changeRequests.qty_changes]
  const qtySupplier = changeRequests.qty_changes[`supplier${suffix}` as keyof typeof changeRequests.qty_changes]

  // Calculate max value for consistent scaling
  const maxValue = Math.max(
    dateCustomer + dateSupplier,
    priceCustomer + priceSupplier,
    qtyCustomer + qtySupplier,
    1 // Minimum to avoid division by zero
  )

  // Handle bar click to navigate to PO Lines filtered by supplier and date range
  // Note: The PO Lines tab URL parameter support is limited to supplier and status_tab.
  // Users can further filter by "Changed" status using the filter panel to see
  // lines with pending change requests (change_proposed_supplier/customer).
  // CR-6310: Include date_from and date_to so drill-through data matches the period.
  const handleBarClick = useCallback((_changeType: string) => {
    if (!drilldownFilters) return

    const params = new URLSearchParams()
    params.set('tab', 'po_lines')
    params.set('status_tab', 'all')  // Show all lines so changed ones are visible
    if (drilldownFilters.supplierId) {
      params.set('supplier', drilldownFilters.supplierId.toString())
    }
    if (drilldownFilters.startDate) {
      params.set('date_from', drilldownFilters.startDate)
    }
    if (drilldownFilters.endDate) {
      params.set('date_to', drilldownFilters.endDate)
    }

    navigate(`/purchasing?${params.toString()}`)
  }, [drilldownFilters, navigate])

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Change Requests - {periodLabel}</CardTitle>
        <div className="flex gap-4 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-blue-500 rounded" />
            <span>Customer Initiated</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-orange-500 rounded" />
            <span>Supplier Initiated</span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ChartBar
          label="Date Changes"
          changeType="date"
          customerValue={dateCustomer}
          supplierValue={dateSupplier}
          maxValue={maxValue}
          onBarClick={drilldownFilters ? handleBarClick : undefined}
        />
        <ChartBar
          label="Price Changes"
          changeType="price"
          customerValue={priceCustomer}
          supplierValue={priceSupplier}
          maxValue={maxValue}
          onBarClick={drilldownFilters ? handleBarClick : undefined}
        />
        <ChartBar
          label="Quantity Changes"
          changeType="qty"
          customerValue={qtyCustomer}
          supplierValue={qtySupplier}
          maxValue={maxValue}
          onBarClick={drilldownFilters ? handleBarClick : undefined}
        />
      </CardContent>
    </Card>
  )
}
